// admindashSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Unauthorized from '../Admin/Unauthorized';
import BaseUrl from '../../BaseUrl';
const headers = Unauthorized.Actionsauthorized(); 

// Async thunks
export const fetchUserCounts = createAsyncThunk(
  'admindash/fetchUserCounts',
  async () => {
    const response = await axios.get(`${BaseUrl.authurl}Auth/GetUserCount`);
    return response.data;
  }
);

// UPDATED: New API endpoint for monthly consultation/walkin footfall
export const fetchMonthlyConsultationData = createAsyncThunk(
  'admindash/fetchMonthlyConsultationData',
  async (year = new Date().getFullYear()) => {
    const response = await axios.get(`${BaseUrl.invoiceurl}Invoices/Year_Monthwise_PatientFootFall?year=${year}`);
    return response.data;
  }
);

// UPDATED: New API endpoint for monthly revenue
export const fetchMonthlyRevenueData = createAsyncThunk(
  'admindash/fetchMonthlyRevenueData',
  async (year = new Date().getFullYear()) => {
    const response = await axios.get(`${BaseUrl.invoiceurl}Invoices/Year_Monthwise_Revenue?year=${year}`);
    return response.data;
  }
);

// --- NEW ASYNC THUNKS ---

export const fetchCurrentMonthConsultationCount = createAsyncThunk(
  'admindash/fetchCurrentMonthConsultationCount',
  async () => {
    const response = await axios.get(`${BaseUrl.consultationurl}Consultations/CurrentMonthConsultationCount`,{headers});
    return response.data;
  }
);

export const fetchCurrentMonthRevenue = createAsyncThunk(
  'admindash/fetchCurrentMonthRevenue',
  async () => {
    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
    const currentYear = currentDate.getFullYear();
    
    const response = await axios.get(`${BaseUrl.invoiceurl}Invoices/Year_Monthwise_Revenue?year=${currentYear}`);
    
    // Calculate total revenue for current month (Consultation + Walk-in)
    const consultationRevenue = response.data.Result?.MonthWiseConsultationRevenue?.[currentMonth] || 0;
    const walkinRevenue = response.data.Result?.MonthWiseWalkinRevenue?.[currentMonth] || 0;
    const totalRevenue = consultationRevenue + walkinRevenue;
    
    return totalRevenue;
  }
);

// Initial state
const initialState = {
  // User counts
  counts: {
    PatientCount: 0,
    DoctorCount: 0,
  },
  
  // --- NEW STATE PROPERTIES ---
  currentMonthConsultationCount: 0,
  currentMonthRevenue: 0,
  selectedFootfallType: 'consultation', // 'consultation' or 'walkin'
  consultationFootfallData: [], // Store consultation footfall data
  walkinFootfallData: [], // Store walkin footfall data
  // -----------------------------

  // Chart data
  footfallData: [],
  revenueData: [],
  
  // UI state
  currentPage: 1,
  currentPageAppointments: 1,
  doctorsPerPage: 6,
  appointmentsPerPage: 6,
  
  // Modal states
  modalVisible: false,
  selectedPatient: null,
  selectedDoctor: null,
  open: false,
  
  // Year selection
  selectedYear: new Date().getFullYear(),
  
  // Loading states
  loading: {
    counts: false,
    footfallData: false,
    revenueData: false,
    currentMonthConsultationCount: false,
    currentMonthRevenue: false,
  },
  
  // Error states
  error: {
    counts: null,
    footfallData: null,
    revenueData: null,
    currentMonthConsultationCount: null,
    currentMonthRevenue: null,
  }
};

// Helper function to transform footfall data from new API format
const transformFootfallData = (apiResponse, type = 'consultation') => {
  const monthOrder = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  const consultationData = apiResponse?.Result?.MonthWiseConsultationFootFall || {};
  const walkinData = apiResponse?.Result?.MonthWiseWalkinFootFall || {};
  
  if (type === 'consultation') {
    return monthOrder.map(month => ({
      month: month.substring(0, 3), // Short month name (Jan, Feb, etc.)
      fullMonth: month,
      count: consultationData[month] || 0
    }));
  } else {
    return monthOrder.map(month => ({
      month: month.substring(0, 3),
      fullMonth: month,
      count: walkinData[month] || 0
    }));
  }
};

// Helper function to transform revenue data from new API format
const transformRevenueData = (apiResponse) => {
  const monthOrder = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  const consultationRevenue = apiResponse?.Result?.MonthWiseConsultationRevenue || {};
  const walkinRevenue = apiResponse?.Result?.MonthWiseWalkinRevenue || {};
  
  return monthOrder.map(month => ({
    month: month.substring(0, 3), // Short month name (Jan, Feb, etc.)
    monthLabel: month,
    consultationRevenue: consultationRevenue[month] || 0,
    walkinRevenue: walkinRevenue[month] || 0,
    monthlyRevenue: (consultationRevenue[month] || 0) + (walkinRevenue[month] || 0)
  }));
};

// Slice
const admindashSlice = createSlice({
  name: 'admindash',
  initialState,
  reducers: {
    // Pagination actions
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setCurrentPageAppointments: (state, action) => {
      state.currentPageAppointments = action.payload;
    },
    
    // Modal actions
    setModalVisible: (state, action) => {
      state.modalVisible = action.payload;
    },
    setSelectedPatient: (state, action) => {
      state.selectedPatient = action.payload;
    },
    setSelectedDoctor: (state, action) => {
      state.selectedDoctor = action.payload;
    },
    setOpen: (state, action) => {
      state.open = action.payload;
    },
    
    // Year selection
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload;
    },
    
    // Footfall type selection
    setSelectedFootfallType: (state, action) => {
      state.selectedFootfallType = action.payload;
      // Update footfall data based on selected type
      if (state.selectedFootfallType === 'consultation') {
        state.footfallData = state.consultationFootfallData;
      } else {
        state.footfallData = state.walkinFootfallData;
      }
    },
    
    // Reset state
    resetAdmindashState: () => initialState,
    
    // Close all modals
    closeAllModals: (state) => {
      state.modalVisible = false;
      state.open = false;
      state.selectedPatient = null;
      state.selectedDoctor = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch User Counts
      .addCase(fetchUserCounts.pending, (state) => {
        state.loading.counts = true;
        state.error.counts = null;
      })
      .addCase(fetchUserCounts.fulfilled, (state, action) => {
        state.loading.counts = false;
        state.counts = action.payload;
      })
      .addCase(fetchUserCounts.rejected, (state, action) => {
        state.loading.counts = false;
        state.error.counts = action.error.message;
      })
      
      // UPDATED: Fetch Monthly Consultation/Walkin Footfall Data with new API
      .addCase(fetchMonthlyConsultationData.pending, (state) => {
        state.loading.footfallData = true;
        state.error.footfallData = null;
      })
      .addCase(fetchMonthlyConsultationData.fulfilled, (state, action) => {
        state.loading.footfallData = false;
        
        // Store both consultation and walkin data separately
        state.consultationFootfallData = transformFootfallData(action.payload, 'consultation');
        state.walkinFootfallData = transformFootfallData(action.payload, 'walkin');
        
        // Set current footfall data based on selected type
        if (state.selectedFootfallType === 'consultation') {
          state.footfallData = state.consultationFootfallData;
        } else {
          state.footfallData = state.walkinFootfallData;
        }
      })
      .addCase(fetchMonthlyConsultationData.rejected, (state, action) => {
        state.loading.footfallData = false;
        state.error.footfallData = action.error.message;
        state.footfallData = [];
      })
      
      // UPDATED: Fetch Monthly Revenue Data with new API
      .addCase(fetchMonthlyRevenueData.pending, (state) => {
        state.loading.revenueData = true;
        state.error.revenueData = null;
      })
      .addCase(fetchMonthlyRevenueData.fulfilled, (state, action) => {
        state.loading.revenueData = false;
        state.revenueData = transformRevenueData(action.payload);
      })
      .addCase(fetchMonthlyRevenueData.rejected, (state, action) => {
        state.loading.revenueData = false;
        state.error.revenueData = action.error.message;
        state.revenueData = [];
      })

      // Current Month Consultation Count
      .addCase(fetchCurrentMonthConsultationCount.pending, (state) => {
        state.loading.currentMonthConsultationCount = true;
        state.error.currentMonthConsultationCount = null;
      })
      .addCase(fetchCurrentMonthConsultationCount.fulfilled, (state, action) => {
        state.loading.currentMonthConsultationCount = false;
        state.currentMonthConsultationCount = action.payload;
      })
      .addCase(fetchCurrentMonthConsultationCount.rejected, (state, action) => {
        state.loading.currentMonthConsultationCount = false;
        state.error.currentMonthConsultationCount = action.error.message;
      })

      // Current Month Revenue
      .addCase(fetchCurrentMonthRevenue.pending, (state) => {
        state.loading.currentMonthRevenue = true;
        state.error.currentMonthRevenue = null;
      })
      .addCase(fetchCurrentMonthRevenue.fulfilled, (state, action) => {
        state.loading.currentMonthRevenue = false;
        state.currentMonthRevenue = action.payload;
      })
      .addCase(fetchCurrentMonthRevenue.rejected, (state, action) => {
        state.loading.currentMonthRevenue = false;
        state.error.currentMonthRevenue = action.error.message;
      });
  },
});

// Export actions
export const {
  setCurrentPage,
  setCurrentPageAppointments,
  setModalVisible,
  setSelectedPatient,
  setSelectedDoctor,
  setOpen,
  setSelectedYear,
  setSelectedFootfallType,
  resetAdmindashState,
  closeAllModals,
} = admindashSlice.actions;

// Export selectors
export const selectAdmindash = (state) => state.admindash;
export const selectCounts = (state) => state.admindash.counts;
export const selectFootfallData = (state) => state.admindash.footfallData;
export const selectRevenueData = (state) => state.admindash.revenueData;
export const selectLoading = (state) => state.admindash.loading;
export const selectError = (state) => state.admindash.error;
export const selectCurrentPage = (state) => state.admindash.currentPage;
export const selectCurrentPageAppointments = (state) => state.admindash.currentPageAppointments;
export const selectModalVisible = (state) => state.admindash.modalVisible;
export const selectSelectedPatient = (state) => state.admindash.selectedPatient;
export const selectSelectedDoctor = (state) => state.admindash.selectedDoctor;
export const selectOpen = (state) => state.admindash.open;
export const selectSelectedYear = (state) => state.admindash.selectedYear;
export const selectSelectedFootfallType = (state) => state.admindash.selectedFootfallType;
export const selectCurrentMonthConsultationCount = (state) => state.admindash.currentMonthConsultationCount;
export const selectCurrentMonthRevenue = (state) => state.admindash.currentMonthRevenue;

export default admindashSlice.reducer;